import {
  ExtractionFieldValue,
  ExtractionRecordValues,
  ExtractionResult,
  ExtractionReviewSchema,
  ExtractionReviewValues,
} from "components/Document/ExtractionReview";
import {
  ApplyExtractedBillOfLadingInput,
  ExtractedBillOfLadingQuery,
  ExtractionStatus,
  FacilityShippingDocumentsForSourceQuery,
  ShippingDocumentLineInput,
  UnitType,
  WasteKind,
} from "generated-graphql/graphql";
import { UNIT_LABELS } from "routes/Customer/Waste/Containers/containerDisplay";
import { toFlexibleNumber } from "util/flexibleNumber";

/** The repeated group key, which is also the extractor's own array field path. */
const LINES = "lines";

const UNIT_OPTIONS = Object.values(UnitType).map((value) => ({
  value,
  label: UNIT_LABELS[value],
}));

/**
 * Distinct from `WasteStream.wasteKind`, which process-manifests computes —
 * this is what the document itself declares. "Blank" (an ordinary line the
 * document says nothing special about) is the grid's own "—" option, not one
 * of these.
 */
export const DECLARED_WASTE_KIND_LABELS: Record<WasteKind, string> = {
  [WasteKind.Rcra]: "RCRA",
  [WasteKind.UniversalWaste]: "Universal Waste",
  [WasteKind.UsedOil]: "Used Oil",
  [WasteKind.Pharmaceutical]: "Pharmaceutical",
};

const DECLARED_WASTE_KIND_OPTIONS = Object.values(WasteKind).map((value) => ({
  value,
  label: DECLARED_WASTE_KIND_LABELS[value],
}));

/**
 * The bill of lading's fields as the reviewer sees them, keyed by the
 * extractor's own field paths — `documentNumber`, `lines[0].description` —
 * so a confidence score addresses a control without any translation, the
 * same posture `WASTE_PROFILE_REVIEW_SCHEMA` (7C) takes.
 *
 * Facility is deliberately not a grid field: decision 4 puts a facility
 * picker in the dialog chrome above the grid instead, since it is confirmed
 * once per document rather than per line.
 */
export const BILL_OF_LADING_REVIEW_SCHEMA: ExtractionReviewSchema = {
  sections: [
    {
      key: "header",
      title: "Bill of Lading",
      fields: [
        { key: "documentNumber", label: "Document number", kind: "text" },
        { key: "generatorName", label: "Generator", kind: "text" },
        { key: "transporterName", label: "Transporter", kind: "text" },
        { key: "destinationName", label: "Destination", kind: "text" },
        {
          key: "shipDate",
          label: "Ship date",
          kind: "text",
          helperText: "YYYY-MM-DD",
        },
      ],
    },
  ],
  rowGroups: [
    {
      key: LINES,
      title: "Lines",
      rowNoun: "line",
      /*
       * The description alone. A line restated at a different quantity is the
       * same waste re-weighed at the scale, which is the ordinary case on this
       * paperwork and reads as a changed cell; including quantity would make it
       * a line the document does not have and ask the reviewer about an
       * addition instead of a change.
       *
       * Carrier paperwork has no standard layout and can repeat a description
       * across lines — two drums of the same waste — so this is not a unique
       * key. `pairRecordRows` holds one queue of extracted rows per identity
       * and takes from the front, so two identical descriptions pair with two
       * separate lines rather than both landing on the first. Where the counts
       * differ, the surplus is reported as rows to keep or remove, which is
       * the question the reviewer should be asked.
       */
      identityColumns: ["description"],
      columns: [
        { key: "description", label: "Description", kind: "text" },
        { key: "quantity", label: "Quantity", kind: "number" },
        { key: "unit", label: "Unit", kind: "select", options: UNIT_OPTIONS },
        {
          key: "declaredWasteKind",
          label: "Declared waste kind",
          kind: "select",
          options: DECLARED_WASTE_KIND_OPTIONS,
        },
        {
          key: "federalWasteCodes",
          label: "Federal waste codes",
          kind: "text",
          helperText: "Comma-separated",
        },
        {
          key: "stateWasteCodes",
          label: "State waste codes",
          kind: "text",
          helperText: "Comma-separated",
        },
      ],
    },
  ],
};

type Reading = NonNullable<ExtractedBillOfLadingQuery["extractedBillOfLading"]>;

/**
 * A shipping document an earlier apply already wrote, as the review dialog
 * reads it back — the record a second review would replace.
 */
export type SavedShippingDocument = NonNullable<
  FacilityShippingDocumentsForSourceQuery["facility"]
>["wasteShippingDocuments"][number];

/**
 * A stored date as the plain `YYYY-MM-DD` text the reading holds `shipDate` in.
 *
 * The wire value is a `Date` scalar, and the grid compares values as text, so
 * comparing the two forms directly would report a difference on every document
 * that has a ship date at all.
 */
function toDateOnlyText(value: unknown): string | null {
  if (value == null) return null;
  const date = value instanceof Date ? value : new Date(String(value));
  return Number.isNaN(date.getTime()) ? null : date.toISOString().slice(0, 10);
}

/**
 * The distinct kinds the reading's lines declare, ordered by the enum rather
 * than by where the lines fall, so one document always presents the same set
 * however its lines happen to be sequenced.
 *
 * What the document declares, not what it means for compliance. Whether a kind
 * counts toward generator status is the engine's answer and varies by state and
 * by kind, so stating it here would restate a rule this surface does not own.
 *
 * Reflects the extraction, not the reviewer's live edits: the form state lives
 * inside `ExtractionReviewGrid`, and reaching into it would mean widening that
 * shared component for one caller. The surrounding copy says the reading is what
 * the extractor found, which keeps that honest.
 */
export function declaredKinds(reading: Pick<Reading, "lines"> | null | undefined): WasteKind[] {
  const declared = new Set(
    (reading?.lines ?? [])
      .map((line) => line.declaredWasteKind)
      .filter((kind): kind is WasteKind => kind != null)
  );
  return Object.values(WasteKind).filter((kind) => declared.has(kind));
}

/**
 * The status and error the review grid should show.
 *
 * A reading carrying an error is not reviewable, and the document's own
 * extraction status does not say so: the enrichment job that wrote the payload
 * succeeded, so the document reads as SUCCEEDED while the candidate holds
 * either the extractor's failure or the resolver's "cannot be read back"
 * message for a payload written under a schema it can no longer parse. Showing
 * the document's status alone leaves the reviewer an empty grid and no reason
 * for it.
 */
export function toGridExtractionState(
  reading: Pick<Reading, "error"> | null | undefined,
  document:
    | { extractionStatus?: ExtractionStatus | null; extractionError?: string | null }
    | null
    | undefined
): { status: ExtractionStatus | null | undefined; extractionError: string | null | undefined } {
  if (reading?.error != null) {
    return { status: ExtractionStatus.Failed, extractionError: reading.error };
  }
  return {
    status: document?.extractionStatus,
    extractionError: document?.extractionError,
  };
}

/** Join codes for the grid's single comma-separated text field. */
function joinCodes(codes: readonly string[] | null | undefined): string | null {
  if (!codes || codes.length === 0) return null;
  return codes.join(", ");
}

/**
 * The reading in the shape `ExtractionReviewGrid` consumes.
 *
 * The API already keys `fieldConfidences` by field path in the same
 * `lines[0].description` form `rowConfidenceKey` builds, so confidence
 * passes through unmapped — the same posture 7C's `toExtractionResult`
 * documents for its own reading.
 */
function toValues(values: Reading["values"]): Record<string, ExtractionFieldValue> {
  return {
    documentNumber: values.documentNumber ?? null,
    generatorName: values.generatorName ?? null,
    transporterName: values.transporterName ?? null,
    destinationName: values.destinationName ?? null,
    // `shipDate` is a `Date` scalar on the wire but a free-typed field here —
    // see `ApplyExtractedBillOfLadingInput`'s doc comment on why the reviewer
    // edits it as text rather than through a date picker.
    shipDate: values.shipDate == null ? null : String(values.shipDate),
  };
}

function toRows(lines: Reading["lines"]): Record<string, Record<string, ExtractionFieldValue>[]> {
  return {
    [LINES]: lines.map((line) => ({
      description: line.description ?? null,
      quantity: line.quantity ?? null,
      unit: line.unit ?? null,
      declaredWasteKind: line.declaredWasteKind ?? null,
      federalWasteCodes: joinCodes(line.federalWasteCodes),
      stateWasteCodes: joinCodes(line.stateWasteCodes),
    })),
  };
}

export function toExtractionResult(reading: Reading): ExtractionResult {
  return {
    // The reviewer's own saved corrections over the model's reading: the
    // resolver runs `applyCandidateCorrections` before returning `values`.
    values: toValues(reading.values),
    rows: toRows(reading.lines),
    // What the model read, uncorrected. The grid shows this as "Suggested: x"
    // beside a field the reviewer has since changed, and offers it back on
    // click. Omitting it makes `extractedBaseline` fall back to `values`, so a
    // second review pass presents a reviewer's own earlier edit as the
    // extraction and the model's reading is gone from the screen.
    extractedValues: toValues(reading.asExtractedValues),
    extractedRows: toRows(reading.asExtractedLines),
    confidence: reading.fieldConfidences
      .filter((entry) => entry.confidence != null)
      .map((entry) => ({ field: entry.fieldPath, score: entry.confidence ?? 0 })),
  };
}

/**
 * The saved shipping document a review is about to be written over, in the
 * grid's own schema-keyed terms.
 *
 * A bill of lading's apply replaces the whole line set, and the review grid is
 * seeded only from the extraction — so without this, a line someone added by
 * hand is absent from the grid and deleted by the confirm, and a header value
 * they corrected is replaced with nothing said. Naming the saved values as the
 * record is what turns both into something the reviewer is asked about.
 *
 * Deliberately not seeding: a stored value is not pre-filled into a reading
 * field, because the document did not say it. This is the opposite claim — here
 * is what you are about to overwrite.
 *
 * `shipDate` is stringified to the same `YYYY-MM-DD` form the reading uses, or
 * the comparison would flag every date as a difference between a `Date` and the
 * text beside it.
 */
export function toRecordValues(
  document: SavedShippingDocument | null | undefined
): ExtractionRecordValues {
  // No saved document is nothing to compare against, which is a different claim
  // from a saved document whose fields happen to be empty.
  if (!document) return {};

  return {
    values: {
      documentNumber: document.documentNumber ?? null,
      generatorName: document.generatorName ?? null,
      transporterName: document.transporterName ?? null,
      destinationName: document.destinationName ?? null,
      shipDate: toDateOnlyText(document.shipDate),
    },
    rows: {
      [LINES]: document.lines.map((line) => ({
        description: line.description ?? null,
        quantity: line.quantity ?? null,
        unit: line.unit ?? null,
        declaredWasteKind: line.declaredWasteKind ?? null,
        federalWasteCodes: joinCodes(line.federalWasteCodes),
        stateWasteCodes: joinCodes(line.stateWasteCodes),
      })),
    },
  };
}

const UNIT_VALUES: readonly string[] = Object.values(UnitType);
const WASTE_KIND_VALUES: readonly string[] = Object.values(WasteKind);

const isUnitType = (value: string): value is UnitType => UNIT_VALUES.includes(value);
const isWasteKind = (value: string): value is WasteKind => WASTE_KIND_VALUES.includes(value);

const toUnit = (value: ExtractionFieldValue | undefined): UnitType | null =>
  typeof value === "string" && isUnitType(value) ? value : null;

const toWasteKind = (value: ExtractionFieldValue | undefined): WasteKind | null =>
  typeof value === "string" && isWasteKind(value) ? value : null;

function toText(value: ExtractionFieldValue | undefined): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
}

/**
 * A number passes through. So does a string the whole of which is a number,
 * including one written with thousands separators. Anything else becomes null
 * rather than reaching the mutation as text the `Float` input would reject by
 * type. The reviewer sees the field empty on the next read, which is the cost of
 * keeping the apply itself from failing on a typo.
 */
function toQuantity(value: ExtractionFieldValue | undefined): number | null {
  const flexible = toFlexibleNumber(value);
  return typeof flexible === "number" ? flexible : null;
}

/**
 * Split a comma-separated codes field back into a clean array.
 *
 * A blank cell is an empty array, never null. The extraction payload types
 * both code fields as non-null arrays and the prompt asks for `[]` when the
 * document shows no codes, so the reviewer has no way to mean "unknown" here —
 * blank means "none printed". Sending null instead would read as a correction
 * away from `[]` on every line that simply has no codes, marking an untouched
 * field as a human edit.
 */
function toCodes(value: ExtractionFieldValue | undefined): string[] {
  if (typeof value !== "string") return [];
  return value
    .split(",")
    .map((code) => code.trim())
    .filter((code) => code !== "");
}

function toLine(row: Record<string, ExtractionFieldValue>): ShippingDocumentLineInput {
  return {
    description: toText(row.description),
    quantity: toQuantity(row.quantity),
    unit: toUnit(row.unit),
    declaredWasteKind: toWasteKind(row.declaredWasteKind),
    federalWasteCodes: toCodes(row.federalWasteCodes),
    stateWasteCodes: toCodes(row.stateWasteCodes),
  };
}

/**
 * The saved lines keyed by the same identity the grid pairs rows on, so a
 * reviewed row can find the record line it stands for.
 *
 * Each description holds a queue rather than one line, and lookups take from
 * the front — the same greedy pairing `pairRecordRows` does, so two drums of
 * the same waste keep their own links instead of both taking the first one's.
 * Normalized the way the grid normalizes an identity (trimmed, lowercased) or
 * the two sides would fail to meet on a difference in case alone.
 */
function savedLinesByDescription(
  saved: SavedShippingDocument | null | undefined
): Map<string, SavedShippingDocument["lines"][number][]> {
  const byDescription = new Map<string, SavedShippingDocument["lines"][number][]>();
  for (const line of saved?.lines ?? []) {
    const key = (line.description ?? "").trim().toLowerCase();
    if (key === "") continue;
    const queue = byDescription.get(key);
    if (queue) queue.push(line);
    else byDescription.set(key, [line]);
  }
  return byDescription;
}

/**
 * A line's stream and container, taken from the saved line it stands for.
 *
 * The review grid has no column for either, so every row it hands back is
 * silent about them. The server fills that silence two ways —
 * `withCarriedLineMatches` carries the links forward by position and matching
 * description, and anything still unset goes to the matcher — and neither is
 * safe for a line the reviewer kept from the record: deleting an extracted row
 * above it shifts positions, the carry misses, and the matcher then guesses a
 * link a human had chosen. Sending the ids explicitly settles it here, because
 * an explicit value wins over both.
 *
 * Only a row the reading has no counterpart for (`extractedIndex` null) gets
 * the links. A row that came from the extraction is already covered by the
 * carry, and claiming its links here would reach past a reviewer who repointed
 * that line.
 *
 * Every row takes its turn in the queue regardless, including an extracted one
 * that gets nothing from it. `pairRecordRows` decides which record row the
 * reviewer sees by consuming extracted rows first, so skipping a turn here
 * would put this walk out of step with that one: an extracted "Drum of waste
 * oil" and a kept second drum would both be answered by the FIRST saved drum,
 * copying the extracted line's container onto the kept line and losing the one
 * a human chose for it.
 */
function linksForKeptRow(
  row: { extractedIndex: number | null; values: Record<string, ExtractionFieldValue> },
  savedByDescription: Map<string, SavedShippingDocument["lines"][number][]>
): { wasteStreamId?: string | null; containerId?: string | null } {
  const key = (toText(row.values.description) ?? "").trim().toLowerCase();
  if (key === "") return {};
  const saved = savedByDescription.get(key)?.shift();
  if (!saved || row.extractedIndex != null) return {};
  return {
    wasteStreamId: saved.wasteStream?.id ?? null,
    containerId: saved.container?.id ?? null,
  };
}

/**
 * The reviewer's confirmed values as an apply input.
 *
 * `shipDate` is passed through as whatever text the reviewer left — trimmed,
 * or null when blank — rather than parsed here. The `Date` scalar's own
 * `parseValue` (`new Date(value)`) is lenient about format, and the field's
 * helper text already steers toward `YYYY-MM-DD`; a string it truly can't
 * read surfaces as a save error naming the field, matching this grid's
 * "server is the validation gate" posture rather than silently nulling a
 * value someone typed.
 *
 * `existingId` carries the shipping document forward on a re-apply (editing
 * an already-saved document's reading again); omitted, the apply creates one.
 */
export function toApplyInput(
  reviewed: ExtractionReviewValues,
  {
    documentId,
    facilityId,
    existingId,
    saved,
  }: {
    documentId: string;
    facilityId: string;
    existingId?: string;
    /**
     * The document this apply will write over, when there is one. Its lines are
     * where a kept record row gets its stream and container back.
     */
    saved?: SavedShippingDocument | null;
  }
): ApplyExtractedBillOfLadingInput {
  const { values, rows } = reviewed;
  const lines = rows[LINES] ?? [];
  // Mutated as rows consume it, so two lines sharing a description take two
  // different saved lines.
  const savedByDescription = savedLinesByDescription(saved);
  return {
    documentId,
    // Sent so the apply records each line's corrections against the extracted
    // line it came from. Without it the server falls back to position, and a
    // line the reviewer deleted or inserted shifts every line below it.
    lineExtractedIndexes: lines.map((line) => line.extractedIndex),
    document: {
      id: existingId,
      facilityId,
      documentNumber: toText(values.documentNumber),
      generatorName: toText(values.generatorName),
      transporterName: toText(values.transporterName),
      destinationName: toText(values.destinationName),
      shipDate: toText(values.shipDate),
      lines: lines.map((line) => ({
        ...toLine(line.values),
        ...linksForKeptRow(line, savedByDescription),
      })),
    },
  };
}
